import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, X } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { cn } from '../lib/utils';

/**
 * Thumbnail that opens a fullscreen lightbox preview for a blob:/http image URL.
 * Used by support chat and SMS media attachments. When objectUrl is a reduced
 * preview, pass fullObjectUrl (and onOpen to start fetching it); the lightbox
 * shows the preview until the full image arrives.
 */
const BlobImageLightbox = ({
  objectUrl,
  fullObjectUrl,
  onOpen,
  alt = 'Image',
  className,
  thumbClassName,
}) => {
  const [open, setOpen] = useState(false);
  const lightboxUrl = fullObjectUrl || objectUrl;

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!objectUrl) return null;

  return (
    <>
      <button
        type="button"
        className={cn(
          'block w-full cursor-zoom-in rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
          onOpen?.();
        }}
        aria-label={`Preview ${alt}`}
      >
        <img
          src={objectUrl}
          alt={alt}
          className={cn(
            'max-h-56 w-full rounded-md object-contain bg-black/5 dark:bg-white/5',
            thumbClassName,
          )}
        />
      </button>

      {open
        && createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            role="presentation"
          >
            <div
              className="relative flex h-[85vh] w-[90vw] max-w-4xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={alt}
            >
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/30 px-4 py-2">
                <span className="truncate text-sm font-medium">{alt}</span>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    asChild
                  >
                    <a href={lightboxUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                      Open in new tab
                    </a>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setOpen(false)}
                    aria-label="Close preview"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex min-h-0 flex-1 items-center justify-center bg-muted/10 p-4">
                <img
                  src={lightboxUrl}
                  alt={alt}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default BlobImageLightbox;
